import { useState, useCallback } from 'react'
import { apiService } from '../services/api'
import { Brand, Model, Version } from '../types'

export interface VersionYear {
 id: string
 versionId: string
 yearId: string
 createdAt: string
 updatedAt: string
 year: {
 id: string
 year: number
 productionStatus: string
 isActive: boolean
 createdAt: string
 updatedAt: string
 }
}

export interface CatalogSearchResult {
 brands: Brand[]
 models: Model[]
 versions: Version[]
}

export const useCatalog = () => {
 const [brands, setBrands] = useState<Brand[]>([])
 const [models, setModels] = useState<Model[]>([])
 const [versions, setVersions] = useState<Version[]>([])
 const [versionYears, setVersionYears] = useState<VersionYear[]>([])
 const [isLoading, setIsLoading] = useState(false)
 const [error, setError] = useState<string | null>(null)

const fetchBrands = useCallback(async (category?: string) => {
  try {
  setIsLoading(true)
  setError(null)
  console.log('🔄 Buscando marcas...', category ? `categoria: ${category}` : '')
  const response = await apiService.getBrands(category)
 console.log('✅ Marcas carregadas:', response)
   // Map API response to Brand type, ensuring label field exists
   const mappedBrands: Brand[] = response.map(brand => ({
     ...brand,
     label: brand.name // Backend returns name, map to label for compatibility
   }))
   setBrands(mappedBrands)
  } catch (err) {
  console.error('❌ Erro ao buscar marcas:', err)
  setError('Erro ao carregar marcas')
  // Em caso de erro, não atualizar brands para evitar loops
  } finally {
  setIsLoading(false)
  }
  }, [])

const fetchBrandModels = useCallback(async (brandId: string) => {
  try {
  setIsLoading(true)
  setError(null)
  const response = await apiService.getBrandModels(brandId)
  // Map API response to Model type with label property
  const mappedModels: Model[] = response.map(model => ({
    ...model,
    label: model.name
  }))
  setModels((prev) => {
  // Evitar duplicatas
  const existingIds = new Set(prev.map(m => m.id))
  const newModels = mappedModels.filter(m => !existingIds.has(m.id))
  return [...prev, ...newModels]
  })
  } catch (err) {
  console.error('Erro ao buscar modelos:', err)
  setError('Erro ao carregar modelos')
  } finally {
  setIsLoading(false)
  }
  }, [])

const fetchModelVersions = async (modelId: string) => {
  try {
  setIsLoading(true)
  setError(null)
  const response = await apiService.getModelVersions(modelId)
  // Map API response to Version type with label property
  const mappedVersions: Version[] = response.map(version => ({
    ...version,
    label: version.name
  }))
  setVersions(mappedVersions)
  } catch (err) {
  console.error('Erro ao buscar versões:', err)
  setError('Erro ao carregar versões')
  } finally {
  setIsLoading(false)
  }
  }

const searchCatalog = async (query: string): Promise<CatalogSearchResult | null> => {
  try {
  setIsLoading(true)
  setError(null)
  const response = await apiService.searchCatalog(query)
  // Map API response to ensure label property exists
  const mappedResponse: CatalogSearchResult = {
    brands: response.brands.map(brand => ({ ...brand, label: brand.name })) as Brand[],
    models: response.models.map(model => ({ ...model, label: model.name })) as Model[],
    versions: response.versions.map(version => ({ ...version, label: version.name })) as Version[]
  }
  return mappedResponse
  } catch (err) {
  console.error('Erro ao buscar no catálogo:', err)
  setError('Erro ao buscar no catálogo')
  return null
  } finally {
  setIsLoading(false)
  }
  }

const getBrandById = async (brandId: string): Promise<Brand | null> => {
  try {
  setIsLoading(true)
  setError(null)
  const response = await apiService.getBrandById(brandId)
  // Map API response to Brand type with label property
  if (response) {
    return { ...response, label: response.name }
  }
  return null
  } catch (err) {
  console.error('Erro ao buscar marca:', err)
  setError('Erro ao carregar marca')
  return null
  } finally {
  setIsLoading(false)
  }
  }

const getModelById = async (modelId: string): Promise<Model | null> => {
  try {
  setIsLoading(true)
  setError(null)
  const response = await apiService.getModelById(modelId)
  // Map API response to Model type with label property
  if (response) {
    return { ...response, label: response.name }
  }
  return null
  } catch (err) {
  console.error('Erro ao buscar modelo:', err)
  setError('Erro ao carregar modelo')
  return null
  } finally {
  setIsLoading(false)
  }
  }

const getVersionById = async (versionId: string): Promise<Version | null> => {
  try {
  setIsLoading(true)
  setError(null)
  const response = await apiService.getVersionById(versionId)
  // Map API response to Version type with label property
  if (response) {
    return { ...response, label: response.name } as Version
  }
  return null
  } catch (err) {
  console.error('Erro ao buscar versão:', err)
  setError('Erro ao carregar versão')
  return null
  } finally {
  setIsLoading(false)
  }
  }

 const fetchVersionYears = async (versionId: string) => {
 try {
 setIsLoading(true)
 setError(null)
 console.log('🔄 Buscando anos da versão:', versionId)
 const response = await apiService.getVersionYears(versionId)
 console.log('✅ Anos carregados:', response)
 setVersionYears(response)
 return response
 } catch (err) {
 console.error('❌ Erro ao buscar anos da versão:', err)
 setError('Erro ao carregar anos')
 setVersionYears([])
 return []
 } finally {
 setIsLoading(false)
 }
 }

 return {
 brands,
 models,
 versions,
 versionYears,
 isLoading,
 error,
 fetchBrands,
 fetchBrandModels,
 fetchModels: fetchBrandModels, // Alias
 fetchModelVersions,
 fetchVersions: fetchModelVersions, // Alias
 fetchVersionYears,
 searchCatalog,
 getBrandById,
 getModelById,
 getVersionById,
 setModels,
 setVersions,
 setVersionYears
 }
}
